import { useState, useCallback, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface ConfirmDialogState extends ConfirmOptions {
  open: boolean;
  resolve: (confirmed: boolean) => void;
}

// Hook — returns a `confirm(options)` function that resolves to true/false.
// Also returns a <ConfirmDialogPortal /> that must be rendered in the component tree.
export function useConfirm() {
  const [state, setState] = useState<ConfirmDialogState | null>(null);
  const resolveRef = useRef<(v: boolean) => void>(() => {});

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ ...options, open: true, resolve });
    });
  }, []);

  const handleClose = (confirmed: boolean) => {
    resolveRef.current(confirmed);
    setState(null);
  };

  const ConfirmDialogPortal = () =>
    state ? (
      <Dialog open={state.open} onClose={() => handleClose(false)} maxWidth="xs" fullWidth>
        {state.title && <DialogTitle>{state.title}</DialogTitle>}
        <DialogContent>
          <DialogContentText>{state.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose(false)} size="small">
            {state.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            onClick={() => handleClose(true)}
            size="small"
            color={state.danger ? 'error' : 'primary'}
            variant="contained"
            autoFocus
          >
            {state.confirmLabel ?? 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    ) : null;

  return { confirm, ConfirmDialogPortal };
}
